import React, { useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Clock } from "@phosphor-icons/react";

const REASONS = [
    "Breakdown",
    "Weather",
    "Equipment failure",
    "Waiting for trucks",
    "Hatch change",
    "Belt stoppage",
    "Power failure",
    "Stoppage - other",
];

const CATEGORIES = [
    { value: "equipment", label: "Equipment" },
    { value: "vessel", label: "Vessel-side" },
    { value: "external", label: "External (weather, etc.)" },
    { value: "operations", label: "Operations" },
    { value: "documentation", label: "Documentation" },
    { value: "other", label: "Other" },
];

const EQUIPMENT_OPTIONS = [
    "Ship Loader 1",
    "Ship Loader 2",
    "Belt 1",
    "Belt 2",
    "Belt 3",
    "Stacker",
    "Reclaimer",
    "Stockpile",
    "Tug",
    "Other",
];

function pad(n) {
    return n.toString().padStart(2, "0");
}

function toLocalInput(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdhocDelayModal({ vessel, onClose, onSubmitted }) {
    const defaults = useMemo(() => {
        const end = new Date();
        const start = new Date(end.getTime() - 30 * 60 * 1000);
        return { start: toLocalInput(start), end: toLocalInput(end) };
    }, []);

    const [start, setStart] = useState(defaults.start);
    const [end, setEnd] = useState(defaults.end);
    const [reason, setReason] = useState(REASONS[0]);
    const [customReason, setCustomReason] = useState("");
    const [actionsTaken, setActionsTaken] = useState("");
    const [location, setLocation] = useState("");
    const [category, setCategory] = useState("equipment");
    const [equipmentId, setEquipmentId] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const startDt = new Date(start);
        const endDt = new Date(end);
        if (endDt <= startDt) {
            toast.error("End time must be after start time");
            return;
        }
        const finalReason = reason === "Stoppage - other" && customReason.trim()
            ? customReason.trim()
            : reason;
        setLoading(true);
        try {
            await api.post(`/vessels/${vessel.id}/delays`, {
                start_time: startDt.toISOString(),
                end_time: endDt.toISOString(),
                reason: finalReason,
                actions_taken: actionsTaken,
                location,
                category,
                equipment_id: equipmentId || null,
            });
            toast.success("Delay logged");
            onSubmitted?.();
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="adhoc-delay-modal">
            <div className="g-card max-w-xl w-full max-h-[90vh] overflow-y-auto">
                <div className="g-card-header-red">
                    <Clock size={18} weight="fill" />
                    <div className="flex-1">
                        <div className="uppercase-label text-[9px] text-white/70">Log a delay</div>
                        <div className="font-display font-bold text-white">{vessel.vessel_name}</div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-sm" data-testid="adhoc-delay-close">
                        Close
                    </button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-4" data-testid="adhoc-delay-form">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="uppercase-label block mb-1.5">Start</label>
                            <input
                                type="datetime-local"
                                value={start}
                                onChange={(e) => setStart(e.target.value)}
                                className="g-input tabular"
                                required
                                data-testid="adhoc-delay-start"
                            />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">End</label>
                            <input
                                type="datetime-local"
                                value={end}
                                onChange={(e) => setEnd(e.target.value)}
                                className="g-input tabular"
                                required
                                data-testid="adhoc-delay-end"
                            />
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="uppercase-label block mb-1.5">Category</label>
                            <select value={category} onChange={(e) => setCategory(e.target.value)} className="g-input" data-testid="adhoc-delay-category">
                                {CATEGORIES.map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Equipment <span className="text-slate-400 normal-case lowercase tracking-normal">(if applicable)</span></label>
                            <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} className="g-input" data-testid="adhoc-delay-equipment">
                                <option value="">—</option>
                                {EQUIPMENT_OPTIONS.map((eq) => (
                                    <option key={eq} value={eq}>{eq}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1.5">Reason</label>
                        <select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="g-input"
                            required
                            data-testid="adhoc-delay-reason"
                        >
                            {REASONS.map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    </div>
                    {reason === "Stoppage - other" && (
                        <div>
                            <label className="uppercase-label block mb-1.5">Specify reason</label>
                            <input
                                value={customReason}
                                onChange={(e) => setCustomReason(e.target.value)}
                                className="g-input"
                                placeholder="e.g. Truck queue"
                                data-testid="adhoc-delay-custom-reason"
                            />
                        </div>
                    )}
                    <div>
                        <label className="uppercase-label block mb-1.5">Action taken <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span></label>
                        <textarea
                            value={actionsTaken}
                            onChange={(e) => setActionsTaken(e.target.value)}
                            className="g-input"
                            rows={2}
                            placeholder="What was done to resolve"
                            data-testid="adhoc-delay-actions"
                        />
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1.5">Location <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span></label>
                        <input
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            className="g-input"
                            placeholder="e.g. Hatch 3, Conveyor B"
                            data-testid="adhoc-delay-location"
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline" data-testid="adhoc-delay-cancel">
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading} data-testid="adhoc-delay-submit">
                            {loading ? "Logging…" : "Log delay"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
